import type { FC, FormEvent, ReactNode } from 'react'
import type { Estimate } from '@/lib/pricing'
import type { CallbackSlotId } from '@/types'

import { useEffect, useRef, useState } from 'react'

import {
    CircleAlert,
    CircleCheck,
    LoaderCircle,
    Phone,
    X
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button, ButtonLink } from '@/components/generals/button'
import { formatEuro, formatPhone } from '@/lib/format'

import { garage } from '@/constants/garage'
import { CALLBACK_SLOTS } from '@/constants/pricing'

interface CallbackDialogProps {
    open: boolean
    onClose: () => void
    estimate: Estimate
    vehicleLabel: string
}

type FieldName = 'name' | 'phone' | 'vehicle'
type FieldErrors = Partial<Record<FieldName, string>>
type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error'

type FormValues = {
    name: string
    phone: string
    vehicle: string
    slot: CallbackSlotId
    company: string
}

const INITIAL_VALUES: FormValues = {
    name: '',
    phone: '',
    vehicle: '',
    slot: 'asap',
    company: ''
}

const PHONE_PATTERN = /^(?:0|\+33|0033)[1-9]\d{8}$/

const SLOT_SENTENCES: Record<CallbackSlotId, string> = {
    asap: 'au plus vite, pendant nos horaires d’ouverture',
    midi: 'entre 12 h et 14 h',
    soir: 'après 17 h'
}

const normalizePhone = (value: string): string => value.replace(/[\s.\-()]/g, '')

const validate = (values: FormValues): FieldErrors => {
    const errors: FieldErrors = {}

    if (values.name.trim().length < 2) errors.name = 'Indiquez votre nom, pour savoir qui rappeler.'
    if (values.name.trim().length > 80) errors.name = '80 caractères maximum.'
    if (!PHONE_PATTERN.test(normalizePhone(values.phone))) errors.phone = 'Ce numéro semble incomplet. Exemple : 06 12 34 56 78.'
    if (values.vehicle.trim().length > 80) errors.vehicle = '80 caractères maximum.'

    return errors
}

const inputClasses = (invalid: boolean): string => cn(
    'h-12 w-full rounded-md border bg-card px-4 text-base text-foreground transition-colors duration-150',
    'placeholder:text-muted-foreground/70 focus-visible:border-foreground focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring',
    invalid ? 'border-destructive' : 'border-input'
)

export const CallbackDialog: FC<CallbackDialogProps> = ({ open, onClose, estimate, vehicleLabel }): ReactNode => {
    const dialogRef = useRef<HTMLDialogElement>(null)
    const formRef = useRef<HTMLFormElement>(null)
    const openedAt = useRef(0)

    const [values, setValues] = useState<FormValues>(INITIAL_VALUES)
    const [touched, setTouched] = useState<Partial<Record<FieldName, boolean>>>({})
    const [attempted, setAttempted] = useState(false)
    const [serverErrors, setServerErrors] = useState<FieldErrors>({})
    const [status, setStatus] = useState<SubmitStatus>('idle')
    const [message, setMessage] = useState('')
    const [confirmedName, setConfirmedName] = useState('')

    useEffect(() => {
        const dialog = dialogRef.current

        if (!dialog) return

        if (open && !dialog.open) {
            openedAt.current = Date.now()
            dialog.showModal()

            // Sur ordinateur, on place directement le curseur dans le premier champ.
            if (window.matchMedia('(pointer: fine)').matches) {
                formRef.current?.querySelector<HTMLInputElement>('input[name=\'name\']')?.focus()
            }
        }

        if (!open && dialog.open) dialog.close()
    }, [open])

    // Les règles du navigateur priment ; le serveur complète ce qu'il a refusé de son côté.
    const errors: FieldErrors = { ...serverErrors, ...validate(values) }

    const visibleError = (field: FieldName): string | undefined => {
        return touched[field] || attempted ? errors[field] : undefined
    }

    const update = (field: keyof FormValues, value: string) => {
        setValues(current => ({ ...current, [field]: value }))
        setServerErrors(current => ({ ...current, [field]: undefined }))
    }

    const handleClose = () => {
        if (status === 'success') {
            setValues(INITIAL_VALUES)
            setTouched({})
            setAttempted(false)
            setStatus('idle')
            setMessage('')
        }

        onClose()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'submitting') return

        setAttempted(true)

        const clientErrors = validate(values)
        const firstInvalid = (['name', 'phone', 'vehicle'] as FieldName[]).find(field => clientErrors[field])

        if (firstInvalid) {
            formRef.current?.querySelector<HTMLInputElement>(`[name='${firstInvalid}']`)?.focus()

            return
        }

        setStatus('submitting')
        setMessage('')

        try {
            const response = await fetch('/api/callback-request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: values.name.trim(),
                    phone: values.phone,
                    vehicle: values.vehicle.trim(),
                    slot: values.slot,
                    company: values.company,
                    vehicleClass: estimate.vehicleClass,
                    services: estimate.lines.map(line => line.id),
                    elapsedMs: Date.now() - openedAt.current
                })
            })

            const payload = await response.json().catch(() => null) as {
                message?: string
                firstName?: string
                fieldErrors?: FieldErrors
            } | null

            if (!response.ok) {
                setServerErrors(payload?.fieldErrors ?? {})
                setStatus('error')
                setMessage(payload?.message ?? `L’envoi n’a pas abouti. Réessayez dans un instant ou appelez-nous au ${garage.phone.display}.`)

                return
            }

            setConfirmedName(payload?.firstName ?? '')
            setStatus('success')
        } catch {
            setStatus('error')
            setMessage(`Connexion interrompue. Vérifiez votre réseau puis réessayez, ou appelez-nous au ${garage.phone.display}.`)
        }
    }

    const nameError = visibleError('name')
    const phoneError = visibleError('phone')
    const vehicleError = visibleError('vehicle')
    const serviceNames = estimate.lines.map(line => line.name.toLowerCase()).join(', ')

    return (
        <dialog
            ref={dialogRef}
            onClose={handleClose}
            onClick={event => {
                if (event.target === dialogRef.current) dialogRef.current?.close()
            }}
            aria-labelledby='callback-title'
            className={cn(
                'mb-0 mt-auto max-h-none w-full max-w-none overflow-visible bg-transparent p-0 text-foreground',
                'sm:m-auto sm:w-[calc(100%-2rem)] sm:max-w-[34rem]',
                'backdrop:bg-ink/60'
            )}
        >
            <div className='max-h-[92dvh] overflow-y-auto rounded-t-2xl border border-border bg-card p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-float sm:max-h-[90dvh] sm:rounded-xl sm:p-7'>
                <div className='flex items-start justify-between gap-4'>
                    <div>
                        <p className='label-mono text-signal-text'>
                            Demande de rappel
                        </p>

                        <h2 id='callback-title' className='heading mt-2 text-subtitle'>
                            {status === 'success' ? 'C’est noté.' : 'On vous rappelle avec ce devis.'}
                        </h2>
                    </div>

                    <button
                        type='button'
                        onClick={() => dialogRef.current?.close()}
                        className='-mr-2 -mt-1 inline-flex size-12 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-foreground/[0.06] hover:text-foreground'
                    >
                        <X aria-hidden='true' className='size-5' />
                        <span className='sr-only'>Fermer</span>
                    </button>
                </div>

                {status === 'success' ? (
                    <div className='mt-6' role='status'>
                        <div className='flex gap-3 rounded-lg border border-border bg-surface/60 p-4'>
                            <CircleCheck aria-hidden='true' className='mt-0.5 size-5 shrink-0 text-success' />

                            <p className='text-sm leading-relaxed'>
                                <span className='font-semibold'>
                                    Merci{confirmedName ? ` ${confirmedName}` : ''}.
                                </span>
                                {' '}Gwenaëlle vous rappelle {SLOT_SENTENCES[values.slot]} au {formatPhone(normalizePhone(values.phone))}.
                                {estimate.lines.length > 0 && ` Votre estimation de ${formatEuro(estimate.totalTtc)} TTC est jointe à la demande.`}
                            </p>
                        </div>

                        <Button className='mt-6 w-full' onClick={() => dialogRef.current?.close()}>
                            Fermer
                        </Button>
                    </div>
                ) : (
                    <form ref={formRef} noValidate onSubmit={handleSubmit} className='mt-6 space-y-5'>
                        <div className='rounded-lg border border-dashed border-border bg-surface/50 px-4 py-3 text-sm'>
                            {estimate.lines.length > 0 ? (
                                <p>
                                    <span className='font-semibold'>{vehicleLabel}</span>
                                    <span className='text-muted-foreground'> · {serviceNames}</span>
                                    <span className='font-mono font-semibold tabular-nums'> · {formatEuro(estimate.totalTtc)} TTC</span>
                                </p>
                            ) : (
                                <p className='text-muted-foreground'>
                                    Aucune intervention cochée&nbsp;: on fait le point ensemble au téléphone.
                                </p>
                            )}
                        </div>

                        <div>
                            <label htmlFor='callback-name' className='font-semibold'>
                                Votre nom
                            </label>

                            <input
                                id='callback-name'
                                name='name'
                                type='text'
                                autoComplete='name'
                                value={values.name}
                                onChange={event => update('name', event.target.value)}
                                onBlur={() => setTouched(current => ({ ...current, name: true }))}
                                aria-invalid={Boolean(nameError)}
                                aria-describedby={nameError ? 'callback-name-error' : undefined}
                                className={cn('mt-2', inputClasses(Boolean(nameError)))}
                            />

                            {nameError && (
                                <p id='callback-name-error' className='mt-1.5 flex items-center gap-1.5 text-sm text-destructive'>
                                    <CircleAlert aria-hidden='true' className='size-4 shrink-0' />
                                    {nameError}
                                </p>
                            )}
                        </div>

                        <div>
                            <label htmlFor='callback-phone' className='font-semibold'>
                                Téléphone
                            </label>

                            <input
                                id='callback-phone'
                                name='phone'
                                type='tel'
                                inputMode='tel'
                                autoComplete='tel'
                                placeholder='06 12 34 56 78'
                                value={values.phone}
                                onChange={event => update('phone', event.target.value)}
                                onBlur={() => setTouched(current => ({ ...current, phone: true }))}
                                aria-invalid={Boolean(phoneError)}
                                aria-describedby={phoneError ? 'callback-phone-error' : undefined}
                                className={cn('mt-2 tabular-nums', inputClasses(Boolean(phoneError)))}
                            />

                            {phoneError && (
                                <p id='callback-phone-error' className='mt-1.5 flex items-center gap-1.5 text-sm text-destructive'>
                                    <CircleAlert aria-hidden='true' className='size-4 shrink-0' />
                                    {phoneError}
                                </p>
                            )}
                        </div>

                        <div>
                            <label htmlFor='callback-vehicle' className='font-semibold'>
                                Modèle ou immatriculation
                                <span className='font-normal text-muted-foreground'> (facultatif)</span>
                            </label>

                            <input
                                id='callback-vehicle'
                                name='vehicle'
                                type='text'
                                autoComplete='off'
                                placeholder='Ex. Peugeot 308 de 2019'
                                value={values.vehicle}
                                onChange={event => update('vehicle', event.target.value)}
                                onBlur={() => setTouched(current => ({ ...current, vehicle: true }))}
                                aria-invalid={Boolean(vehicleError)}
                                aria-describedby={vehicleError ? 'callback-vehicle-error' : 'callback-vehicle-hint'}
                                className={cn('mt-2', inputClasses(Boolean(vehicleError)))}
                            />

                            {vehicleError ? (
                                <p id='callback-vehicle-error' className='mt-1.5 flex items-center gap-1.5 text-sm text-destructive'>
                                    <CircleAlert aria-hidden='true' className='size-4 shrink-0' />
                                    {vehicleError}
                                </p>
                            ) : (
                                <p id='callback-vehicle-hint' className='mt-1.5 text-sm text-muted-foreground'>
                                    Avec l’immatriculation, le prix exact est prêt avant l’appel.
                                </p>
                            )}
                        </div>

                        <fieldset>
                            <legend className='font-semibold'>
                                Quand vous rappeler&nbsp;?
                            </legend>

                            <div className='mt-2 grid gap-2 sm:grid-cols-3'>
                                {CALLBACK_SLOTS.map(slot => (
                                    <label
                                        key={slot.id}
                                        className={cn(
                                            'flex min-h-12 cursor-pointer items-center justify-center rounded-md border px-3 text-center text-sm font-medium transition-colors duration-150',
                                            'has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-ring',
                                            values.slot === slot.id ? 'border-foreground bg-foreground/[0.04]' : 'border-border hover:border-foreground/40'
                                        )}
                                    >
                                        <input
                                            type='radio'
                                            name='slot'
                                            value={slot.id}
                                            checked={values.slot === slot.id}
                                            onChange={() => update('slot', slot.id)}
                                            className='sr-only'
                                        />
                                        {slot.label}
                                    </label>
                                ))}
                            </div>
                        </fieldset>

                        <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                            <label htmlFor='callback-company'>Société</label>
                            <input
                                id='callback-company'
                                name='company'
                                type='text'
                                tabIndex={-1}
                                autoComplete='off'
                                value={values.company}
                                onChange={event => update('company', event.target.value)}
                            />
                        </div>

                        {status === 'error' && message && (
                            <div role='alert' className='flex gap-3 rounded-lg border border-destructive/40 bg-destructive/[0.06] p-4 text-sm'>
                                <CircleAlert aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-destructive' />
                                <p>{message}</p>
                            </div>
                        )}

                        <div className='space-y-3 pt-1'>
                            <Button type='submit' size='lg' className='w-full' disabled={status === 'submitting'}>
                                {status === 'submitting' ? (
                                    <>
                                        <LoaderCircle aria-hidden='true' className='size-5 motion-safe:animate-spin' />
                                        Envoi en cours
                                    </>
                                ) : 'Envoyer ma demande'}
                            </Button>

                            <ButtonLink href={garage.phone.href} variant='ghost' className='w-full tabular-nums'>
                                <Phone aria-hidden='true' className='size-4' />
                                Ou appelez le {garage.phone.display}
                            </ButtonLink>
                        </div>

                        <p className='text-xs leading-relaxed text-muted-foreground'>
                            Vos coordonnées servent uniquement à vous rappeler pour ce devis. Aucune prospection, aucune revente.
                        </p>
                    </form>
                )}
            </div>
        </dialog>
    )
}
